import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Car, AlertCircle, Check } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-river-cabanas.jpg";

const logistics = [
  { icon: Calendar, title: "Best booking window", body: "Memorial Day weekend cabanas typically sell out 4–8 weeks ahead. Last-minute openings happen — call 877-577-7667." },
  { icon: Clock, title: "Property hours", body: "Day cabanas: 10:30 AM – 6:30 PM. Glamping check-in: 3 PM. Tubing shuttle runs continuously during day hours." },
  { icon: Car, title: "Parking & access", body: "Every cabana includes a dedicated parking spot — no offsite shuttle lots, no walking from the highway. Extra parking $24.99/car." },
  { icon: AlertCircle, title: "What to expect on River Road", body: "Public access points hit capacity by 11 AM Saturday. Son's caps daily capacity by design — your spot is reserved." },
];

const MemorialDayNewBraunfels = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Memorial Day Weekend on the Guadalupe River — Logistics & Booking"
        description="Memorial Day weekend at Son's Guadalupe near New Braunfels: logistics, parking, capacity caps, last-minute cabanas, and shuttle-assisted tubing."
        canonical="/memorial-day-weekend-new-braunfels"
        ogImage="/og/blog-memorial-day.jpg"
      />
      <Header />
      <main className="pt-24">
        <section className="relative h-[50vh] min-h-[380px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Memorial Day weekend on the Guadalupe River at Son's Guadalupe" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <p className="text-coral-orange font-medium mb-3">Holiday weekend logistics</p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
                  Memorial Day Weekend on the Guadalupe River
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  Where to park, when to arrive, and how to skip the crowds — the practical guide to Memorial Day weekend at Son's Guadalupe near New Braunfels.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Memorial Day weekend is the unofficial start of summer on the Guadalupe River. Public access points (Hueco Springs, the Second Crossing, Guadalupe River State Park) hit capacity by mid-morning. River Road backs up. Tubing-outfitter shuttle lines stretch across parking lots.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Son's Guadalupe is built around skipping that. We cap daily capacity, every cabana includes its own parking spot, and tubing floats end right at your private cabana — no shuttle lines, no long walks. Here's how to plan your weekend.
            </p>
          </div>
        </section>

        <section className="section-padding bg-cream">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 text-center">Memorial Day logistics at a glance</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {logistics.map((l) => (
                <div key={l.title} className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <l.icon className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{l.title}</h3>
                  <p className="text-charcoal/70">{l.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-6">What to book for Memorial Day weekend</h2>
            <ul className="space-y-3 text-muted-foreground text-lg">
              {[
                "Day cabana — shaded basecamp for 8–12, BBQ + picnic table + dedicated parking, from $69",
                "In-water covered picnic table — for groups of 6 with 1 car parking included",
                "Overnight glamping bell tent — AC, real beds, sleeps 5, from $99/night",
                "Tubing add-on — $24.99/person",
                "Group buyout — call for full-property reservations during peak weekends",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section-padding bg-teal text-white">
          <div className="container-narrow mx-auto px-4 md:px-8 text-center max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Lock in your Memorial Day spot</h2>
            <p className="text-white/85 text-lg mb-6">Holiday weekends sell out — book direct for the best availability and price.</p>
            <Link to="/book-now"><Button variant="hero" size="xl">Check Availability</Button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MemorialDayNewBraunfels;
